import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Spinner, Alert, Button, Modal, Form } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaCode, FaRocket } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchCategories,
    fetchMicrocontrollersByCategory,
    fetchFirmwareVersions,
    fetchFirmwareCode,
    addFirmwareVersion,
    updateFirmwareVersion,
    deleteFirmwareVersion,
    deployFirmware
} from '../redux/actions/FirmwareAction';

const FirmwareManagement = () => {
    const dispatch = useDispatch();
    const { categories: reduxCategories, loading: reduxLoading, error: reduxError } = useSelector(state => state.firmware || {});

    // Data states
    const [categories, setCategories] = useState([]);
    const [microcontrollers, setMicrocontrollers] = useState([]);
    const [allFirmwareVersions, setAllFirmwareVersions] = useState([]);
    const [filteredVersions, setFilteredVersions] = useState([]);

    // Filter states
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedMicrocontroller, setSelectedMicrocontroller] = useState('');
    const [filteredMicrocontrollers, setFilteredMicrocontrollers] = useState([]);

    // UI states
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewCodeModal, setShowViewCodeModal] = useState(false);
    const [showDeployModal, setShowDeployModal] = useState(false);

    // Form states
    const [formData, setFormData] = useState({
        version_number: '',
        description: '',
        release_notes: '',
        firmware_code: '',
        status: 'draft'
    });
    const [editingVersion, setEditingVersion] = useState(null);
    const [deletingVersion, setDeletingVersion] = useState(null);
    const [viewingCode, setViewingCode] = useState(null);
    const [deployingVersion, setDeployingVersion] = useState(null);
    const [deploymentNotes, setDeploymentNotes] = useState('');
    const [isDeploying, setIsDeploying] = useState(false);

    // Fetch all data on component mount
    useEffect(() => {
        fetchAllData();
    }, []);

    // Filter microcontrollers when category changes
    useEffect(() => {
        if (selectedCategory) {
            const filtered = microcontrollers.filter(mc => mc.category === parseInt(selectedCategory));
            setFilteredMicrocontrollers(filtered);
            setSelectedMicrocontroller('');
            setFilteredVersions([]);
        } else {
            setFilteredMicrocontrollers([]);
            setSelectedMicrocontroller('');
            setFilteredVersions([]);
        }
    }, [selectedCategory, microcontrollers]);

    // Filter versions when microcontroller changes
    useEffect(() => {
        if (selectedMicrocontroller) {
            const filtered = allFirmwareVersions.filter(v => v.microcontroller === parseInt(selectedMicrocontroller));
            setFilteredVersions(filtered);
        } else {
            setFilteredVersions([]);
        }
    }, [selectedMicrocontroller, allFirmwareVersions]);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch categories and microcontrollers using Redux actions
            const [categoriesData, microcontrollersData] = await Promise.all([
                dispatch(fetchCategories()),
                dispatch(fetchMicrocontrollersByCategory())
            ]);
            
            // Create a map of category names to IDs for easy lookup
            const categoryNameToIdMap = {};
            categoriesData.forEach(cat => {
                categoryNameToIdMap[cat.name] = cat.id;
            });
            
            // Extract microcontrollers with category association
            // microcontrollersData is grouped by category name: { "IoT Sensors": [...], "Smart Home": [...] }
            const allMicrocontrollers = [];
            Object.entries(microcontrollersData).forEach(([categoryName, mcList]) => {
                const categoryId = categoryNameToIdMap[categoryName];
                mcList.forEach(mc => {
                    allMicrocontrollers.push({
                        ...mc,
                        category: categoryId
                    });
                });
            });

            setCategories(categoriesData);
            setMicrocontrollers(allMicrocontrollers);
            
            // Fetch all firmware versions for all microcontrollers
            const allVersions = [];
            for (const mc of allMicrocontrollers) {
                try {
                    const versions = await dispatch(fetchFirmwareVersions(mc.identifier));
                    allVersions.push(...versions);
                } catch (err) {
                    console.warn(`Failed to fetch versions for ${mc.identifier}:`, err);
                }
            }
            setAllFirmwareVersions(allVersions);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to fetch data from backend');
            setLoading(false);
        }
    };

    // CRUD Operations
    const handleAddVersion = async () => {
        if (!selectedMicrocontroller) {
            alert('Please select a microcontroller first');
            return;
        }

        try {
            const firmwareData = {
                microcontrollerId: parseInt(selectedMicrocontroller),
                versionNumber: formData.version_number,
                description: formData.description,
                releaseNotes: formData.release_notes,
                textCode: formData.firmware_code,
                fileType: 'text',
                status: formData.status,
                isLatest: false
            };

            await dispatch(addFirmwareVersion(firmwareData));
            setSuccessMessage('Firmware version added successfully!');
            setShowAddModal(false);
            resetForm();
            fetchAllData();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error adding version:', err);
            alert('Failed to add firmware version: ' + (err.message || 'Unknown error'));
        }
    };

    const handleEditVersion = async () => {
        try {
            const updateData = {
                version_number: formData.version_number,
                description: formData.description,
                release_notes: formData.release_notes,
                firmware_code: formData.firmware_code,
                status: formData.status
            };

            await dispatch(updateFirmwareVersion(editingVersion.id, updateData));
            setSuccessMessage('Firmware version updated successfully!');
            setShowEditModal(false);
            resetForm();
            fetchAllData();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error updating version:', err);
            alert('Failed to update firmware version: ' + (err.message || 'Unknown error'));
        }
    };

    const handleDeleteVersion = async () => {
        try {
            await dispatch(deleteFirmwareVersion(deletingVersion.id));
            setSuccessMessage('Firmware version deleted successfully!');
            setShowDeleteModal(false);
            setDeletingVersion(null);
            fetchAllData();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            console.error('Error deleting version:', err);
            alert('Failed to delete firmware version: ' + (err.message || 'Unknown error'));
        }
    };

    const handleViewCode = async (version) => {
        try {
            const versionData = await dispatch(fetchFirmwareCode(version.id));
            setViewingCode(versionData);
            setShowViewCodeModal(true);
        } catch (err) {
            console.error('Error fetching code:', err);
            alert('Failed to fetch firmware code: ' + (err.message || 'Unknown error'));
        }
    };

    const openAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    const openEditModal = (version) => {
        setEditingVersion(version);
        setFormData({
            version_number: version.version_number,
            description: version.description || '',
            release_notes: version.release_notes || '',
            firmware_code: version.firmware_code || '',
            status: version.status
        });
        setShowEditModal(true);
    };

    const openDeleteModal = (version) => {
        setDeletingVersion(version);
        setShowDeleteModal(true);
    };

    const openDeployModal = (version) => {
        setDeployingVersion(version);
        setDeploymentNotes('');
        setShowDeployModal(true);
    };

    const handleDeployFirmware = async () => {
        if (!deployingVersion) return;

        try {
            setIsDeploying(true);

            const mcName = getMicrocontrollerName(parseInt(selectedMicrocontroller));
            const result = await dispatch(
                deployFirmware(
                    deployingVersion.id,
                    selectedMicrocontroller,
                    mcName,
                    'OTA'
                )
            );

            setSuccessMessage(`Firmware deployment initiated! ${result.message || 'Deployment started'}`);
            setShowDeployModal(false);
            setDeployingVersion(null);
            setDeploymentNotes('');
            setIsDeploying(false);
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            console.error('Error deploying firmware:', err);
            alert('Failed to deploy firmware: ' + (err.message || 'Unknown error'));
            setIsDeploying(false);
        }
    };

    const resetForm = () => {
        setFormData({
            version_number: '',
            description: '',
            release_notes: '',
            firmware_code: '',
            status: 'draft'
        });
        setEditingVersion(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Helper functions
    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    const getMicrocontrollerName = (mcId) => {
        const mc = microcontrollers.find(m => m.id === mcId);
        return mc ? mc.name : 'Unknown';
    };

    if (loading) {
        return (
            <Container className="mt-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">Loading firmware data...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">
                    <Alert.Heading>Error Loading Data</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <p className="mb-0">
                        Make sure the backend server is running and accessible.
                    </p>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col>
                    <h2>Firmware Management</h2>
                </Col>
            </Row>

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            {/* Filters Section */}
            <Row className="mb-4">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label><strong>Select Category</strong></Form.Label>
                        <Form.Select 
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">-- Select Category --</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>
                                    {cat.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label><strong>Select Microcontroller</strong></Form.Label>
                        <Form.Select 
                            value={selectedMicrocontroller}
                            onChange={(e) => setSelectedMicrocontroller(e.target.value)}
                            disabled={!selectedCategory}
                        >
                            <option value="">-- Select Microcontroller --</option>
                            {filteredMicrocontrollers.map(mc => (
                                <option key={mc.id} value={mc.id}>
                                    {mc.name} ({mc.identifier})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <Button 
                        variant="primary" 
                        onClick={openAddModal}
                        disabled={!selectedMicrocontroller}
                    >
                        <FaPlus className="me-2" />
                        Add New Version
                    </Button>
                </Col>
            </Row>

            {/* Firmware Versions Table */}
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h5>Firmware Versions {selectedMicrocontroller && `(${filteredVersions.length})`}</h5>
                        </Card.Header>
                        <Card.Body>
                            {!selectedMicrocontroller ? (
                                <Alert variant="info">
                                    Please select a category and microcontroller to view firmware versions
                                </Alert>
                            ) : filteredVersions.length === 0 ? (
                                <Alert variant="warning">
                                    No firmware versions found for this microcontroller
                                </Alert>
                            ) : (
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Version</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Latest</th>
                                            <th>Created At</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredVersions.map(version => (
                                            <tr key={version.id}>
                                                <td><strong>{version.version_number}</strong></td>
                                                <td>{version.description || '-'}</td>
                                                <td>
                                                    <Badge bg={
                                                        version.status === 'active' ? 'success' :
                                                        version.status === 'deprecated' ? 'warning' :
                                                        version.status === 'draft' ? 'secondary' : 'info'
                                                    }>
                                                        {version.status}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {version.is_latest ? (
                                                        <Badge bg="success">Latest</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">Old</Badge>
                                                    )}
                                                </td>
                                                <td>{new Date(version.created_at).toLocaleString()}</td>
                                                <td>
                                                    <Button 
                                                        variant="info" 
                                                        size="sm" 
                                                        className="me-2"
                                                        onClick={() => handleViewCode(version)}
                                                        title="View Code"
                                                    >
                                                        <FaEye />
                                                    </Button>
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="me-2"
                                                        onClick={() => openDeployModal(version)}
                                                        title="Deploy to Microcontroller"
                                                    >
                                                        <FaRocket />
                                                    </Button>
                                                    <Button 
                                                        variant="warning" 
                                                        size="sm" 
                                                        className="me-2"
                                                        onClick={() => openEditModal(version)}
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button 
                                                        variant="danger" 
                                                        size="sm"
                                                        onClick={() => openDeleteModal(version)}
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Add Version Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Firmware Version</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Version Number *</Form.Label>
                            <Form.Control
                                type="text"
                                name="version_number"
                                value={formData.version_number}
                                onChange={handleInputChange}
                                placeholder="e.g., 1.0.0"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Brief description of this version"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Release Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="release_notes"
                                value={formData.release_notes}
                                onChange={handleInputChange}
                                placeholder="Detailed release notes"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Firmware Code *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                name="firmware_code"
                                value={formData.firmware_code}
                                onChange={handleInputChange}
                                placeholder="Paste your firmware code here..."
                                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                                <option value="testing">Testing</option>
                                <option value="deprecated">Deprecated</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAddVersion}>
                        Add Version
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Version Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Firmware Version</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Version Number *</Form.Label>
                            <Form.Control
                                type="text"
                                name="version_number"
                                value={formData.version_number}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Release Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="release_notes"
                                value={formData.release_notes}
                                onChange={handleInputChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Firmware Code *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                name="firmware_code"
                                value={formData.firmware_code}
                                onChange={handleInputChange}
                                style={{ fontFamily: 'monospace', fontSize: '12px' }}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                                <option value="testing">Testing</option>
                                <option value="deprecated">Deprecated</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleEditVersion}>
                        Update Version
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Code Modal */}
            <Modal show={showViewCodeModal} onHide={() => setShowViewCodeModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaCode className="me-2" />
                        Firmware Code - {viewingCode?.version_number}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewingCode && (
                        <>
                            <div className="mb-3">
                                <strong>Description:</strong> {viewingCode.description || 'N/A'}
                            </div>
                            <div className="mb-3">
                                <strong>Status:</strong>{' '}
                                <Badge bg={
                                    viewingCode.status === 'active' ? 'success' :
                                    viewingCode.status === 'deprecated' ? 'warning' : 'secondary'
                                }>
                                    {viewingCode.status}
                                </Badge>
                            </div>
                            <hr />
                            <div className="mb-2"><strong>Firmware Code:</strong></div>
                            <pre style={{
                                backgroundColor: '#f5f5f5',
                                padding: '15px',
                                borderRadius: '5px',
                                maxHeight: '500px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '12px'
                            }}>
                                {viewingCode.firmware_code || 'No code available'}
                            </pre>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewCodeModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete firmware version <strong>{deletingVersion?.version_number}</strong>?
                    <br />
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDeleteVersion}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Deploy Firmware Modal */}
            <Modal show={showDeployModal} onHide={() => setShowDeployModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaRocket className="me-2" />
                        Deploy Firmware to Microcontroller
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deployingVersion && (
                        <>
                            <Alert variant="info">
                                <strong>Firmware Version:</strong> {deployingVersion.version_number}
                                <br />
                                <strong>Microcontroller:</strong> {getMicrocontrollerName(parseInt(selectedMicrocontroller))}
                                <br />
                                <strong>Status:</strong>{' '}
                                <Badge bg={
                                    deployingVersion.status === 'active' ? 'success' :
                                    deployingVersion.status === 'deprecated' ? 'warning' : 'secondary'
                                }>
                                    {deployingVersion.status}
                                </Badge>
                            </Alert>

                            <Form.Group className="mb-3">
                                <Form.Label>Deployment Notes (Optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={deploymentNotes}
                                    onChange={(e) => setDeploymentNotes(e.target.value)}
                                    placeholder="Add any notes about this deployment..."
                                />
                                <Form.Text className="text-muted">
                                    These notes will be saved with the deployment record for tracking purposes.
                                </Form.Text>
                            </Form.Group>

                            <Alert variant="warning">
                                <strong>⚠️ Important:</strong> 
                                <ul className="mb-0 mt-2">
                                    <li>Make sure the target microcontroller is connected and ready</li>
                                    <li>The deployment process may take several minutes</li>
                                    <li>Do not disconnect the device during deployment</li>
                                    <li>The firmware will be sent via MQTT/WebSocket to the device</li>
                                </ul>
                            </Alert>

                            {isDeploying && (
                                <div className="text-center my-3">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2">Deploying firmware...</p>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDeployModal(false)}
                        disabled={isDeploying}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleDeployFirmware}
                        disabled={isDeploying}
                    >
                        {isDeploying ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Deploying...
                            </>
                        ) : (
                            <>
                                <FaRocket className="me-2" />
                                Deploy Now
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default FirmwareManagement;